import download from 'downloadjs';
import queryString from 'querystring';
import {SLOT_STATES} from '../constants';
import Configuration from '../config';
import {normalizeBookingPricingFields} from '../util/booking-payload';

const slotsRequestControllers = new Map();

function buildSlotsRequestKey(scope, partnerId, resourceId, bookingPartner, startIso, endIso) {
  return `${scope}:${partnerId}:${resourceId || ''}:${bookingPartner}:${startIso}:${endIso}`;
}

function beginSlotsRequest(requestKey) {
  const previous = slotsRequestControllers.get(requestKey);
  if (previous) {
    previous.abort();
  }
  const controller = new AbortController();
  slotsRequestControllers.set(requestKey, controller);
  return controller;
}

function endSlotsRequest(requestKey, controller) {
  if (slotsRequestControllers.get(requestKey) === controller) {
    slotsRequestControllers.delete(requestKey);
  }
}

export async function login(credential) {
  const res = await fetch(`${Configuration.apiEndPoint}/login`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(credential),
    mode: 'cors'
  });

  const json = await res.json();
  return {json, status: res.status};
}

export async function consumeMobileSsoToken(code) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/mobile/sso-token/consume`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({code}),
      mode: 'cors'
    });
    const json = await res.json().catch(() => ({}));
    return {json, status: res.status};
  } catch (error) {
    return {json: {error: error.message}, status: -1};
  }
}

export async function googleLogin(credential) {
  const res = await fetch(`${Configuration.apiEndPoint}/login/google`, {
    method: 'POST',
    headers: {Accept: 'application/json', 'Content-Type': 'application/json'},
    body: JSON.stringify({credential}),
    mode: 'cors'
  });
  const json = await res.json();
  return {json, status: res.status};
}

export async function googleRegister(data) {
  const res = await fetch(`${Configuration.apiEndPoint}/login/google/register`, {
    method: 'POST',
    headers: {Accept: 'application/json', 'Content-Type': 'application/json'},
    body: JSON.stringify(data),
    mode: 'cors'
  });
  const json = await res.json();
  return {json, status: res.status};
}

export async function appleLogin(data) {
  const res = await fetch(`${Configuration.apiEndPoint}/login/apple`, {
    method: 'POST',
    headers: {Accept: 'application/json', 'Content-Type': 'application/json'},
    body: JSON.stringify(data),
    mode: 'cors'
  });
  return {json: await res.json(), status: res.status};
}

export async function appleRegister(data) {
  const res = await fetch(`${Configuration.apiEndPoint}/login/apple/register`, {
    method: 'POST',
    headers: {Accept: 'application/json', 'Content-Type': 'application/json'},
    body: JSON.stringify(data),
    mode: 'cors'
  });
  return {json: await res.json(), status: res.status};
}

export async function appleLinkExisting(data) {
  const res = await fetch(`${Configuration.apiEndPoint}/login/apple/link-existing`, {
    method: 'POST',
    headers: {Accept: 'application/json', 'Content-Type': 'application/json'},
    body: JSON.stringify(data),
    mode: 'cors'
  });
  return {json: await res.json(), status: res.status};
}

export async function getAuthMethods(token) {
  const res = await fetch(`${Configuration.apiEndPoint}/users/auth-methods`, {
    headers: {Accept: 'application/json', Authorization: `Bearer ${token}`}
  });
  return {json: await res.json(), status: res.status};
}

export async function linkGoogle(credential, token) {
  const res = await fetch(`${Configuration.apiEndPoint}/users/auth-methods/google`, {
    method: 'POST',
    headers: {Accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${token}`},
    body: JSON.stringify({credential})
  });
  return {json: await res.json(), status: res.status};
}

export async function unlinkGoogle(token) {
  const res = await fetch(`${Configuration.apiEndPoint}/users/auth-methods/google`, {
    method: 'DELETE',
    headers: {Accept: 'application/json', Authorization: `Bearer ${token}`}
  });
  return {json: await res.json(), status: res.status};
}

export async function linkApple(data, token) {
  const res = await fetch(`${Configuration.apiEndPoint}/users/auth-methods/apple`, {
    method: 'POST',
    headers: {Accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${token}`},
    body: JSON.stringify(data)
  });
  return {json: await res.json(), status: res.status};
}

export async function unlinkApple(token) {
  const res = await fetch(`${Configuration.apiEndPoint}/users/auth-methods/apple`, {
    method: 'DELETE', headers: {Accept: 'application/json', Authorization: `Bearer ${token}`}
  });
  return {json: await res.json(), status: res.status};
}

export async function setLoginPassword(data, token) {
  const res = await fetch(`${Configuration.apiEndPoint}/users/auth-methods/password`, {
    method: 'PUT',
    headers: {Accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${token}`},
    body: JSON.stringify(data)
  });
  return {json: await res.json(), status: res.status};
}

export async function changePassword(credential, token) {
  const res = await fetch(`${Configuration.apiEndPoint}/users/change_pwd`, {
    method: 'PUT',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify(credential),
    mode: 'cors'
  });

  const json = await res.json();
  return {json, status: res.status};
}

export async function signUpPlayer(signUpData) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/players`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(signUpData)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function signUpPartner(signUpData) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/partners`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(signUpData)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getPartnerProfile(signUpData, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/partners?uid=${signUpData.uid}&email=${signUpData.email}&role=${signUpData.role}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getPlayerProfile(signUpData, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/players?uid=${signUpData.uid}&email=${signUpData.email}&role=${signUpData.role}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function savePlayerProfile(profile, token) {
  try {
    profile.email = profile.user.email;
    const res = await fetch(`${Configuration.apiEndPoint}/players`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(profile)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getPlayerSportRankings(token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/players/rankings`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const text = await res.text();
    const json = text ? JSON.parse(text) : {};
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function savePlayerSportRankingQuestionnaire(sport, answers, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/players/rankings/${sport}/questionnaire`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({answers})
    });
    const text = await res.text();
    let json = {};
    try {
      json = text ? JSON.parse(text) : {};
    } catch (parseError) {
      json = {error: text || parseError.message};
    }
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function savePlayerSportRankingFederation(sport, federation, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/players/rankings/${sport}/federation`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(federation)
    });
    const text = await res.text();
    let json = {};
    try {
      json = text ? JSON.parse(text) : {};
    } catch (parseError) {
      json = {error: text || parseError.message};
    }
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function requestPlayerSportRankingVerification(sport, partnerId, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/players/rankings/${sport}/verification_requests`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({partnerId})
    });
    const text = await res.text();
    let json = {};
    try {
      json = text ? JSON.parse(text) : {};
    } catch (parseError) {
      json = {error: text || parseError.message};
    }
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getOpenMatches(filters, token) {
  try {
    const params = queryString.stringify({
      sport: filters && filters.sport ? filters.sport : '',
      partnerId: filters && filters.partnerId ? filters.partnerId : '',
      from: filters && filters.from ? filters.from : '',
      to: filters && filters.to ? filters.to : ''
    });
    const res = await fetch(`${Configuration.apiEndPoint}/open_matches?${params}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getPublicOpenMatches(filters) {
  try {
    const params = queryString.stringify({
      sport: filters && filters.sport ? filters.sport : '',
      partnerId: filters && filters.partnerId ? filters.partnerId : '',
      from: filters && filters.from ? filters.from : '',
      to: filters && filters.to ? filters.to : '',
      limit: filters && filters.limit ? filters.limit : ''
    });
    const res = await fetch(`${Configuration.apiEndPoint}/open_matches/public?${params}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getMyOpenMatches(token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/open_matches/mine`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function createOpenMatch(payload, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/open_matches`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function createPartnerOpenMatch(payload, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/partner/open_matches`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getPartnerOpenMatches(token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/partner/open_matches`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function quoteOpenMatch(payload, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/open_matches/quote`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function quotePartnerOpenMatch(payload, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/partner/open_matches/quote`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function joinOpenMatch(openMatchId, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/open_matches/${openMatchId}/join`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function leaveOpenMatch(openMatchId, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/open_matches/${openMatchId}/leave`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function updateOpenMatch(openMatchId, payload, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/open_matches/${openMatchId}`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function cancelOpenMatch(openMatchId, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/open_matches/${openMatchId}`, {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getPublicOpenMatch(publicId) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/open_matches/public/${encodeURIComponent(publicId)}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getMyOpenMatchByPublicId(publicId, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/open_matches/public/${encodeURIComponent(publicId)}/me`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getOpenMatchInviteSuggestions(openMatchId, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/open_matches/${openMatchId}/invite_suggestions`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function createOpenMatchInvitation(openMatchId, invitedUid, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/open_matches/${openMatchId}/invitations`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({invitedUid})
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function createPartnerOpenMatchInvitations(openMatchId, payload, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/open_matches/${openMatchId}/invitations/bulk`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function submitOpenMatchResult(openMatchId, payload, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/open_matches/${openMatchId}/result`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function confirmOpenMatchResult(resultId, accepted, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/open_match_results/${resultId}/confirm`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({accepted})
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function saveOpenMatchFeedback(openMatchId, feedback, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/open_matches/${openMatchId}/feedback`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({feedback})
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function savePartnerProfile(profile, token) {
  try {
    const data = {
      name: profile.name,
      address: profile.address,
      province: profile.province,
      CAP: profile.CAP,
      city: profile.city,
      phoneNumber: profile.phoneNumber,
      description: profile.description,
      extDescription: profile.extDescription,
      openingHour: profile.openingHour,
      defaultVisibilityCalendar: profile.defaultVisibilityCalendar,
      facilities: profile.facilities,
      website: profile.website,
      facebook: profile.facebook,
      instagram: profile.instagram,
      youtube: profile.youtube,
      visible: profile.visible
    };
    const res = await fetch(`${Configuration.apiEndPoint}/partners`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(data)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function deleteImageFile(token, file) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/images`, {
      method: 'DELETE',
      headers: {Authorization: `Bearer ${token}`},
      body: JSON.stringify({filenames: [file]})
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getPartnerFields(partnerId, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/partners/${partnerId}/fields`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getPartnerServices(partnerId, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/partners/${partnerId}/services`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function savePartnerField(fieldData, token) {
  try {
    const payload = Object.assign({}, {
      sport: fieldData.sport,
      name: fieldData.name,
      description: fieldData.description,
      covered: fieldData.covered,
      terrain: fieldData.terrain,
      bookable: fieldData.bookable,
      bookingPolicy: fieldData.bookingPolicy,
      additionalCost: fieldData.additionalCost,
      termOfUse: fieldData.termOfUse,
      unbookableHours: fieldData.unbookableHours,
      affiliationsIds: fieldData.affiliationsIds
    });

    const res = await fetch(`${Configuration.apiEndPoint}/partners/fields`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function updatePartnerField(fieldData, token) {
  try {
    const payload = Object.assign({}, {
      sport: fieldData.sport,
      name: fieldData.name,
      description: fieldData.description,
      covered: fieldData.covered,
      terrain: fieldData.terrain,
      bookable: fieldData.bookable,
      bookingPolicy: fieldData.bookingPolicy,
      additionalCost: fieldData.additionalCost,
      termOfUse: fieldData.termOfUse,
      unbookableHours: fieldData.unbookableHours,
      affiliationsIds: fieldData.affiliationsIds
    });
    
    const res = await fetch(`${Configuration.apiEndPoint}/partners/fields/${fieldData.id}`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function deletePartnerField(token, fieldId) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/partners/fields/${fieldId}`, {
      method: 'DELETE',
      headers: {Authorization: `Bearer ${token}`}
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function savePartnerService(serviceData, token) {
  try {
    const payload = Object.assign({}, {
      briefDescription: serviceData.briefDescription,
      name: serviceData.name,
      description: serviceData.description,
      bookable: serviceData.bookable,
      bookingPolicy: serviceData.bookingPolicy,
      additionalCost: serviceData.additionalCost,
      termOfUse: serviceData.termOfUse,
      unbookableHours: serviceData.unbookableHours,
      affiliationsIds: serviceData.affiliationsIds
    });

    const res = await fetch(`${Configuration.apiEndPoint}/partners/services`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function updatePartnerService(serviceData, token) {
  try {
    const payload = Object.assign({}, {
      briefDescription: serviceData.briefDescription,
      name: serviceData.name,
      description: serviceData.description,
      bookable: serviceData.bookable,
      bookingPolicy: serviceData.bookingPolicy,
      additionalCost: serviceData.additionalCost,
      termOfUse: serviceData.termOfUse,
      unbookableHours: serviceData.unbookableHours,
      affiliationsIds: serviceData.affiliationsIds
    });
    
    const res = await fetch(`${Configuration.apiEndPoint}/partners/services/${serviceData.id}`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function deletePartnerService(token, serviceId) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/partners/services/${serviceId}`, {
      method: 'DELETE',
      headers: {Authorization: `Bearer ${token}`}
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function deleteFieldImageFile(token, file, fieldId) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/partners/fields/${fieldId}/images`, {
      method: 'DELETE',
      headers: {Authorization: `Bearer ${token}`},
      body: JSON.stringify({filenames: [file]})
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function deleteServiceImageFile(token, file, serviceId) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/partners/services/${serviceId}/images`, {
      method: 'DELETE',
      headers: {Authorization: `Bearer ${token}`},
      body: JSON.stringify({filenames: [file]})
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getFieldImage(fieldId, fileName, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/partners/fields/${fieldId}/images?load=${fileName}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    const blob = await res.blob();
    return {blob, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getFieldSlots(partnerId, token, fieldId, bookingPartner = false, targetDate = new Date(), searchRange = 'MONTH') {
  let requestKey = null;
  let controller = null;
  try {
    let startDatetime = null;
    let endDatetime = null;

    if (searchRange === 'WEEK') {
      // Nel calendario del player la visualizzazione copre al massimo la settimana
      // Carico allora la settimana della data passata
      // Calcolo data inizio settimana
      const day = targetDate.getDay();
      startDatetime = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate() + (day === 0 ? -6 : 1) - day);
      // Calcolo data fine settimana
      endDatetime = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate() + (day === 0 ? 0 : 7) - day, 23, 59);
    } else if (searchRange === 'DAY') {
      //Carico solo la giornata selezionata
      startDatetime = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate());
      endDatetime = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate(), 23, 59);
    } else {
      // Nel calendario del Partner la visualizzazione copre al massimo il mese
      // Carico allora il mese della data passata
      // Calcolo data inizio mese
      startDatetime = new Date(targetDate.getFullYear(), targetDate.getMonth(), 1 - 6);
      // Calcolo data fine mese
      endDatetime = new Date(targetDate.getFullYear(), targetDate.getMonth() + 1, 6, 23, 59);
    }

    const params = {
      fieldId,
      bookingPartner,
      startDatetime: startDatetime.toISOString(),
      endDatetime: endDatetime.toISOString()
    };
    requestKey = buildSlotsRequestKey('field', partnerId, fieldId, bookingPartner, params.startDatetime, params.endDatetime);
    controller = beginSlotsRequest(requestKey);
    const res = await fetch(`${Configuration.apiEndPoint}/partners/${partnerId}/fields/slots?${queryString.stringify(params)}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      signal: controller.signal
    });
    const json = await res.json();
    endSlotsRequest(requestKey, controller);
    return {json, status: res.status};
  } catch (error) {
    if (error.name === 'AbortError') {
      return {status: 0, json: {error: 'request_cancelled', cancelled: true}};
    }
    return {status: -1, json: {error: error.message}};
  } finally {
    if (requestKey && controller) {
      endSlotsRequest(requestKey, controller);
    }
  }
}

export async function getServiceSlots(partnerId, token, serviceId, bookingPartner = false, targetDate = new Date(), searchRange = 'MONTH') {
  let requestKey = null;
  let controller = null;
  try {
    let startDatetime = null;
    let endDatetime = null;

    if (searchRange === 'WEEK') {
      // Nel calendario del player la visualizzazione copre al massimo la settimana
      // Carico allora la settimana della data passata
      // Calcolo data inizio settimana
      const day = targetDate.getDay();
      startDatetime = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate() + (day === 0 ? -6 : 1) - day);
      // Calcolo data fine settimana
      endDatetime = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate() + (day === 0 ? 0 : 7) - day, 23, 59);
    } else {
      // Nel calendario del Partner la visualizzazione copre al massimo il mese
      // Carico allora il mese della data passata
      // Calcolo data inizio mese
      startDatetime = new Date(targetDate.getFullYear(), targetDate.getMonth(), 1 - 6);
      // Calcolo data fine mese
      endDatetime = new Date(targetDate.getFullYear(), targetDate.getMonth() + 1, 6, 23, 59);
    }

    const params = {
      serviceId,
      bookingPartner,
      startDatetime: startDatetime.toISOString(),
      endDatetime: endDatetime.toISOString()
    };
    requestKey = buildSlotsRequestKey('service', partnerId, serviceId, bookingPartner, params.startDatetime, params.endDatetime);
    controller = beginSlotsRequest(requestKey);
    const res = await fetch(`${Configuration.apiEndPoint}/partners/${partnerId}/services/slots?${queryString.stringify(params)}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      signal: controller.signal
    });
    const json = await res.json();
    endSlotsRequest(requestKey, controller);
    return {json, status: res.status};
  } catch (error) {
    if (error.name === 'AbortError') {
      return {status: 0, json: {error: 'request_cancelled', cancelled: true}};
    }
    return {status: -1, json: {error: error.message}};
  } finally {
    if (requestKey && controller) {
      endSlotsRequest(requestKey, controller);
    }
  }
}

export async function saveFieldSlot(slotData, token) {
  try {
    console.log(slotData);
    const res = await fetch(`${Configuration.apiEndPoint}/slots`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(slotData)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function updateFieldSlot(slotData, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/slots/${slotData.slotId}`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(slotData)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function deleteFieldSlots(slotData, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/slots`, {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(slotData)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getPartners(token, businessName = null) {
  try {
    const query = businessName == null || businessName === '' ? '' : `?businessName=${encodeURIComponent(businessName)}`;
    const res = await fetch(`${Configuration.apiEndPoint}/partners${query}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getSlot(token, slotId) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/admin/slots/transactions/${slotId}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function deleteSlot(token, slotId) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/admin/slots/${slotId}`, {
      method: 'DELETE',
      headers: {Authorization: `Bearer ${token}`},
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getBookings(token, targetDate = null, searchRange = 'MONTH', resourceType = 'field') {
  try {
    let startDatetime = null;
    let endDatetime = null;

    if (targetDate) {
      if (searchRange === 'WEEK') {
        // Nel calendario del player la visualizzazione copre al massimo la settimana
        // Carico allora la settimana della data passata
        // Calcolo data inizio  settimana
        const day = targetDate.getDay();
        startDatetime = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate() + (day === 0 ? -6 : 1) - day);
        // Calcolo data fine settimana
        endDatetime = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate() + (day === 0 ? 0 : 7) - day, 23, 59);
      } else {
        // Nel calendario del Partner la visualizzazione copre al massimo il mese
        // Carico allora il mese della data passata
        // Calcolo data inizio mese
        startDatetime = new Date(targetDate.getFullYear(), targetDate.getMonth(), 1 - 6);
        // Calcolo data fine mese
        endDatetime = new Date(targetDate.getFullYear(), targetDate.getMonth() + 1, 6, 23, 59);
      }
    }
    let res;
    if(resourceType === 'field')
      res = await fetch(`${Configuration.apiEndPoint}/bookings?${queryString.stringify({startDatetime: startDatetime ? startDatetime.toISOString() : null, endDatetime: endDatetime ? endDatetime.toISOString() : null})}`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
    else
      res = await fetch(`${Configuration.apiEndPoint}/bookings/services?${queryString.stringify({startDatetime: startDatetime ? startDatetime.toISOString() : null, endDatetime: endDatetime ? endDatetime.toISOString() : null})}`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
    let json = await res.json();
    if(resourceType === 'all'){
      const res2 = await fetch(`${Configuration.apiEndPoint}/bookings?${queryString.stringify({startDatetime: startDatetime ? startDatetime.toISOString() : null, endDatetime: endDatetime ? endDatetime.toISOString() : null})}`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      json = [...await res2.json(), ...json];
      json.sort(function(a,b){
        return new Date(a.startDatetime) - new Date(b.startDatetime);
      });
    }
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

const sanitizeDedicatedPlanForBooking = plan => ({
  id: plan.id || null,
  months: plan.months,
  price: plan.price,
  active: Boolean(plan.active),
  label: plan.label || null,
  promotionEndsAt: plan.promotionEndsAt || null,
  autoRenew: Boolean(plan.autoRenew)
});

const sanitizeRtvChannelForBooking = channel => {
  const {publicId, ...writableChannel} = channel;
  return {
    ...writableChannel,
    dedicatedPlans: (channel.dedicatedPlans || []).map(sanitizeDedicatedPlanForBooking)
  };
};

const sanitizeSlotForBooking = slot => {
  if (!slot) {
    return slot;
  }
  return {
    ...slot,
    rtvChannels: (slot.rtvChannels || []).map(sanitizeRtvChannelForBooking)
  };
};

const sanitizeBookingPayload = bookingData => {
  const normalized = normalizeBookingPricingFields(bookingData);
  const payload = {
    ...normalized,
    startSlot: sanitizeSlotForBooking(normalized.startSlot),
    endSlot: sanitizeSlotForBooking(normalized.endSlot),
    rtvChannels: (normalized.rtvChannels || []).map(sanitizeRtvChannelForBooking)
  };
  if (payload.matchFormat === null || payload.matchFormat === undefined || payload.matchFormat === '') {
    delete payload.matchFormat;
  }
  return payload;
};

export async function saveBooking(bookingData, token) {
  try {
    const payload = sanitizeBookingPayload(bookingData);
    const res = await fetch(`${Configuration.apiEndPoint}/bookings`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message === 'invalid_booking_pricing' ? 'invalid_booking_pricing' : 'booking_request_failed'}};
  }
}

export async function saveBookingSeries(bookingData, token) {
  try {
    const untilDate = bookingData.subscriptionUntilDate
      ? new Date(bookingData.subscriptionUntilDate)
      : null;
    const payload = sanitizeBookingPayload({
      ...bookingData,
      subscriptionUntilDate: untilDate
        ? `${untilDate.getFullYear()}-${String(untilDate.getMonth() + 1).padStart(2, '0')}-${String(untilDate.getDate()).padStart(2, '0')}`
        : null
    });
    const res = await fetch(`${Configuration.apiEndPoint}/booking-series`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function updateBookingSeries(seriesId, bookingData, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/booking-series/${seriesId}`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(bookingData)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function deleteBookingSeries(seriesId, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/booking-series/${seriesId}`, {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getDedicatedFieldCostQuote(bookingData, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/bookings/dedicated-field-cost/quote`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(normalizeBookingPricingFields({
        slots: bookingData.slots,
        affiliationPricingMode: 'dedicated_field_cost',
        pricingAffiliationId: bookingData.pricingAffiliationId,
        pricingParticipantCount: bookingData.pricingParticipantCount,
        invitedPlayers: [],
        invitedGuests: []
      }))
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message === 'invalid_booking_pricing' ? 'invalid_booking_pricing' : 'booking_request_failed'}};
  }
}

export async function getBookingTopupInvitation(invitationToken, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/booking-topup-invitations/${encodeURIComponent(invitationToken)}`, {
      headers: {Accept: 'application/json', Authorization: `Bearer ${token}`},
      mode: 'cors'
    });
    return {json: await res.json(), status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function acceptBookingTopupInvitation(invitationToken, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/booking-topup-invitations/${encodeURIComponent(invitationToken)}/accept`, {
      method: 'POST',
      headers: {Accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${token}`},
      mode: 'cors'
    });
    return {json: await res.json(), status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function rejectBookingTopupInvitation(invitationToken, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/booking-topup-invitations/${encodeURIComponent(invitationToken)}/reject`, {
      method: 'POST',
      headers: {Accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${token}`},
      mode: 'cors'
    });
    return {json: await res.json(), status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getNotifications(token, limit = 30) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/notifications?limit=${limit}`, {
      headers: {Accept: 'application/json', Authorization: `Bearer ${token}`},
      mode: 'cors'
    });
    return {json: await res.json(), status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function readNotification(notificationId, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/notifications/${notificationId}/read`, {
      method: 'POST',
      headers: {Accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${token}`},
      mode: 'cors'
    });
    return {json: await res.json(), status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function readAllNotifications(token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/notifications/read-all`, {
      method: 'POST',
      headers: {Accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${token}`},
      mode: 'cors'
    });
    return {json: await res.json(), status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getNotificationPreferences(token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/notifications/preferences`, {
      headers: {Accept: 'application/json', Authorization: `Bearer ${token}`},
      mode: 'cors'
    });
    return {json: await res.json(), status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function deleteNotification(notificationId, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/notifications/${notificationId}`, {
      method: 'DELETE',
      headers: {Accept: 'application/json', Authorization: `Bearer ${token}`},
      mode: 'cors'
    });
    return {json: await res.json(), status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function saveNotificationPreferences(preferences, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/notifications/preferences`, {
      method: 'PUT',
      headers: {Accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${token}`},
      body: JSON.stringify(preferences),
      mode: 'cors'
    });
    return {json: await res.json(), status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getWebPushPublicKey(token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/notifications/web-push/public-key`, {
      headers: {Accept: 'application/json', Authorization: `Bearer ${token}`},
      mode: 'cors'
    });
    return {json: await res.json(), status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function deleteReadNotifications(token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/notifications/read`, {
      method: 'DELETE',
      headers: {Accept: 'application/json', Authorization: `Bearer ${token}`},
      mode: 'cors'
    });
    return {json: await res.json(), status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function saveWebPushSubscription(subscription, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/notifications/web-push/subscriptions`, {
      method: 'POST',
      headers: {Accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${token}`},
      body: JSON.stringify(subscription),
      mode: 'cors'
    });
    return {json: await res.json(), status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getBookingMessages(bookingId, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/bookings/${bookingId}/messages`, {
      headers: {Accept: 'application/json', Authorization: `Bearer ${token}`},
      mode: 'cors'
    });
    return {json: await res.json(), status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function sendBookingMessage(bookingId, body, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/bookings/${bookingId}/messages`, {
      method: 'POST',
      headers: {Accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${token}`},
      body: JSON.stringify({body}),
      mode: 'cors'
    });
    return {json: await res.json(), status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getOpenMatchMessages(openMatchId, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/open_matches/${openMatchId}/messages`, {
      headers: {Accept: 'application/json', Authorization: `Bearer ${token}`},
      mode: 'cors'
    });
    return {json: await res.json(), status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function sendOpenMatchMessage(openMatchId, body, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/open_matches/${openMatchId}/messages`, {
      method: 'POST',
      headers: {Accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${token}`},
      body: JSON.stringify({body}),
      mode: 'cors'
    });
    return {json: await res.json(), status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function saveServiceBooking(bookingData, token) {
  try {
    const payload = sanitizeBookingPayload(bookingData);
    const res = await fetch(`${Configuration.apiEndPoint}/bookings/services`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message === 'invalid_booking_pricing' ? 'invalid_booking_pricing' : 'booking_request_failed'}};
  }
}

export async function updateBooking(bookingData, token) {
  try {
    const payload = sanitizeBookingPayload(bookingData);
    const res = await fetch(`${Configuration.apiEndPoint}/bookings/${bookingData.bookingId}`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message === 'invalid_booking_pricing' ? 'invalid_booking_pricing' : 'booking_request_failed'}};
  }
}

export async function updateServiceBooking(bookingData, token) {
  try {
    const payload = sanitizeBookingPayload(bookingData);
    const res = await fetch(`${Configuration.apiEndPoint}/bookings/services/${bookingData.bookingId}`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message === 'invalid_booking_pricing' ? 'invalid_booking_pricing' : 'booking_request_failed'}};
  }
}

export async function updateUserBooking(bookingData, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/user_bookings/${bookingData.id}`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(bookingData)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function deleteBooking(bookingId, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/bookings/${bookingId}`, {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function deleteServiceBooking(bookingId, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/bookings/services/${bookingId}`, {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function deleteUserBooking(userBookingId, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/user_bookings/${userBookingId}`, {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function deleteServiceUserBooking(userBookingId, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/user_bookings/services/${userBookingId}`, {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function deletePlayer(token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/players`, {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function sendResetPwd(email) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/users/send_reset_pwd`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(email)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function verifyEmail(token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/verify_email/${token}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function confirmChangeEmail(token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/players/change_email/${token}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function resetPassword(data, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/users/reset_pwd/${token}`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function addToBookingQueue(slotId, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/booking_queues`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({slotId})
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function topUpWallet(topUpdata, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/admin/user/wallet`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(topUpdata)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function purchaseVideo(slots, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/slots/video`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({slots})
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function purchasePartnerVideo(videoId, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/video_partners/buy`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({videoId})
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getVideo(token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/players/new_videos`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getVideoStreaming(token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/players/new_videos_in_streaming`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    json.sort(function(a,b){
      return new Date(b.startDatetime) - new Date(a.startDatetime);
    });
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function printReport(filters, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/partners/reports?start=${filters.start || ''}&end=${filters.end || ''}&field=${filters.field || ''}&service=${filters.service || ''}&bookings_type=${filters.bookingsType || SLOT_STATES.available}&email=${filters.email || ''}&surname=${filters.surname || ''}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'Content-Disposition': 'attachment; filename=report.xls'
      }
    });
    if (res.status === 200) {
      const blob = await res.blob();
      download(blob, 'report.xls');
      return {status: res.status};
    } else {
      const json = await res.json();
      return {json, status: res.status};
    }
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getDedicatedFieldCostReport(filters, token, exportFile = false) {
  try {
    const params = new URLSearchParams();
    if (filters.start) params.set('start', filters.start);
    if (filters.end) params.set('end', filters.end);
    if (filters.playerIds && filters.playerIds.length) params.set('playerIds', filters.playerIds.join(','));
    if (filters.affiliationIds && filters.affiliationIds.length) params.set('affiliationIds', filters.affiliationIds.join(','));
    if (filters.sport) params.set('sport', filters.sport);
    if (filters.status) params.set('status', filters.status);
    params.set('format', exportFile ? 'xlsx' : 'json');
    const res = await fetch(`${Configuration.apiEndPoint}/partners/reports/dedicated-field-cost?${params.toString()}`, {
      method: 'GET',
      headers: {
        Accept: exportFile ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    if (res.status === 200 && exportFile) {
      download(await res.blob(), `ReplayerCostoCampoDedicato_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.xlsx`);
      return {status: res.status};
    }
    return {json: await res.json(), status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function archiveBooking(bookingId, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/bookings/${bookingId}/archive`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function reportAllPlayers(token) {
  try {
    const fileName = `ReplayerListaPlayer_${new Date().toISOString().slice(0,10).replace(/-/g,"")}.xls`;
    const res = await fetch(`${Configuration.apiEndPoint}/players/all`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'Content-Disposition': 'attachment; filename=report.xls'
      }
    });
    if (res.status === 200) {
      const blob = await res.blob();
      download(blob, fileName);
    }

    return {status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function addVideoToPlayerArea(slotId, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/players/add_video/from_slot/${slotId}`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function addPartnerVideoToPlayerArea(videoId, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/players/add_partner_video/${videoId}`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function requestVideo(slotId, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/partners/request_video/${slotId}`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getPlayerPermanentVideos(token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/players/permanent_videos`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getPlayerHighlightFolders(token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/players/highlight_folders`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getPlayerVideoFolders(token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/players/video_folders`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function createPlayerHighlightFolder(name, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/players/highlight_folders`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({name})
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function createPlayerVideoFolder(name, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/players/video_folders`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({name})
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function deletePlayerHighlightFolder(folderId, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/players/highlight_folders/${folderId}`, {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function deletePlayerVideoFolder(folderId, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/players/video_folders/${folderId}`, {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function highlightFolderSharing(folderId, sharing, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/players/highlight_folders/sharing`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({folderId, sharing})
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function movePlayerVideoToFolder(videoId, folderId, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/players/permanent_videos/${videoId}/video_folder`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({folderId})
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function movePlayerHighlightToFolder(videoId, folderId, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/players/permanent_videos/${videoId}/highlight_folder`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({folderId})
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getPlayerPermanentPartnerVideos(token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/players/permanent_partner_videos`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function confirmPlayerAffiliation(affiliationId, playerId, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/affiliations/${affiliationId}/players_email?playerId=${playerId}&token=${token}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getPlayerPlaylists(token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/players/playlist_videos`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function createHighlightPlaylist(data, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/players/highlight_playlists`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(data)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function exportPlaylist(playlistId, token, force = false) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/players/playlists/${playlistId}/export`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({force})
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function deletePlayerPermanentVideo(videoId, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/players/delete_permanent_videos/${videoId}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function deletePlayerPermanentPartnerVideo(videoId, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/user_video_partners/${videoId}`, {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}


export async function deletePlaylist(playlistId, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/players/delete_playlist/${playlistId}`, {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

// videoData: {name, description}
export async function modifyPlayerPermanentVideo(videoId, videoData, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/players/change_info_permanent_videos/${videoId}`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(videoData)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

// videoData: {name, description}
export async function modifyPlaylist(playlistId, videoData, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/players/change_info_playlist/${playlistId}`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(videoData)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

// videoSegment: {name, description, start, duration*, end*} (*: solo un campo dei due, o duration o end)
export async function cutPlayerPermanentVideo(videoId, videoSegment, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/players/cut_videos/${videoId}`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(videoSegment)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function cutPlayerSliceVideo(slotId, videoSegment, token) {
  try {
    if (!slotId || !/^\d+$/.test(String(slotId))) {
      return {status: -1, json: {error: `invalid_slot_id:${slotId}`}};
    }

    const res = await fetch(`${Configuration.apiEndPoint}/players/slots/${slotId}/cut_slices`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(videoSegment)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function createPlaylist(videoId, videoSegment, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/players/create_playlist/${videoId}`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(videoSegment)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function createPlaylistForSingleCamera(videoId, videoSegment, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/players/create_playlist_single_camera/${videoId}`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(videoSegment)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}
export async function reportTransactions(filters, token) {
  try {
    const fileName = `ReplayerTransazioni_${new Date().toISOString().slice(0,10).replace(/-/g,"")}.xls`;
    const res = await fetch(`${Configuration.apiEndPoint}/players/transactions?start=${filters.start ? new Date(filters.start).toISOString().slice(0,10) : ''}&end=${filters.end ? new Date(filters.end).toISOString().slice(0,10) : ''}&email=${filters.email || ''}&surname=${filters.surname || ''}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'Content-Disposition': 'attachment; filename=report.xls'
      }
    });
    if (res.status === 200) {
      const blob = await res.blob();
      download(blob, fileName);
    }

    return {status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function adminReportSubscriptions(filters, token) {
  try {
    const fileName = `ReplayerAbbonamenti_${new Date().toISOString().slice(0,10).replace(/-/g,"")}.xls`;
    const res = await fetch(`${Configuration.apiEndPoint}/subscriptions/report?start=${filters.start ? new Date(filters.start).toISOString().slice(0,10) : ''}&end=${filters.end ? new Date(filters.end).toISOString().slice(0,10) : ''}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'Content-Disposition': 'attachment; filename=report.xls'
      }
    });
    if (res.status === 200) {
      const blob = await res.blob();
      download(blob, fileName);
    }

    return {status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function adminReportUsage(filters, token) {
  try {
    const fileName = `ReplayerUtilizzo_${filters.reportType}_${new Date().toISOString().slice(0,10).replace(/-/g,"")}.xls`;
    const res = await fetch(`${Configuration.apiEndPoint}/admin/usage_report?start=${filters.start ? new Date(filters.start).toISOString().slice(0,10) : ''}&end=${filters.end ? new Date(filters.end).toISOString().slice(0,10) : ''}&reportType=${filters.reportType}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'Content-Disposition': 'attachment; filename=report.xls'
      }
    });
    if (res.status === 200) {
      const blob = await res.blob();
      download(blob, fileName);
    }

    return {status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function reportBookings(filters, token) {
  try {
    const fileName = `ReplayerPrenotazioni_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.xls`;
    const res = await fetch(`${Configuration.apiEndPoint}/bookings/archived_bookings?start=${filters.start ? new Date(filters.start).toISOString().slice(0, 10) : ''}&end=${filters.end ? new Date(filters.end).toISOString().slice(0, 10) : ''}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'Content-Disposition': 'attachment; filename=report.xls'
      }
    });
    if (res.status === 200) {
      const blob = await res.blob();
      download(blob, fileName);
    }

    return {status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function reportSubscriptions(filters, token) {
  try {
    const fileName = `ReplayerAbbonamenti_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.xls`;
    const res = await fetch(`${Configuration.apiEndPoint}/player/archived_subscriptions?start=${filters.start ? new Date(filters.start).toISOString().slice(0, 10) : ''}&end=${filters.end ? new Date(filters.end).toISOString().slice(0, 10) : ''}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'Content-Disposition': 'attachment; filename=report.xls'
      }
    });
    if (res.status === 200) {
      const blob = await res.blob();
      download(blob, fileName);
    }

    return {status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function reportBookingSeries(filters, token) {
  try {
    const fileName = `ReplayerAbbonamentiCampiServizi_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.xlsx`;
    const query = queryString.stringify({
      start: filters.start || '',
      end: filters.end || ''
    });
    const res = await fetch(`${Configuration.apiEndPoint}/booking-series/report?${query}`, {
      method: 'GET',
      headers: {
        Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        Authorization: `Bearer ${token}`
      }
    });
    if (res.status === 200) {
      download(await res.blob(), fileName);
    }
    return {status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function partnerReportPlayers(token) {
  try {
    const fileName = `ReplayerPlayers_${new Date().toISOString().slice(0,10).replace(/-/g,"")}.xls`;
    const res = await fetch(`${Configuration.apiEndPoint}/partners/players_report`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'Content-Disposition': 'attachment; filename=report.xls'
      }
    });
    if (res.status === 200) {
      const blob = await res.blob();
      download(blob, fileName);
    }

    return {status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getFavouritePartners(token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/players/get_favourite_partners`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function addPartnerToFavourite(uid, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/players/add_favourite_partner`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({uid})
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function removePartnerFromFavourite(uid, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/players/remove_favourite_partner/${uid}`, {
      method: 'DELETE',
      headers: {Authorization: `Bearer ${token}`}
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getPlayers(surname, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/players/list?surname=${surname}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function playerGetPlayers(nickname, name, surname, affiliated, slotId, partnerId, token, playersNumber = null, matchFormat = null) {
  try {
    const params = new URLSearchParams({
      nickname: nickname || '',
      name: name || '',
      surname: surname || '',
      affiliated: affiliated === true ? 'true' : 'false',
      slotId: slotId == null ? '' : String(slotId),
      partnerId: partnerId == null ? '' : String(partnerId),
      ...(playersNumber == null ? {} : {playersNumber: String(playersNumber)}),
      ...(matchFormat == null ? {} : {matchFormat: String(matchFormat)})
    });
    const res = await fetch(`${Configuration.apiEndPoint}/players/players_list?${params.toString()}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function partnerGetPlayers(nickname, name, surname, affiliated, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/partners/players_list?nickname=${nickname}&name=${name}&surname=${surname}&affiliated=${affiliated}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function permanentVideosSharing(videoId, sharing, playlistId, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/players/permanent_videos/sharing`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({videoId, sharing, playlistId})
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getSharedVideo(sharingId, token) {
  try {
    const headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    const res = await fetch(`${Configuration.apiEndPoint}/players/permanent_videos/sharing/${sharingId}`, {
      method: 'GET',
      headers
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function createPartner(partner, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/partners`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(partner)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

// Subscriptions

export async function getPlayerSubscriptions(token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/player/subscriptions`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function setPlayerSubscription(data, token, invoiceRequested = false) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/player/subscriptions`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'X-Replayer-Invoice-Requested': invoiceRequested ? 'true' : 'false'
      },
      body: JSON.stringify(data)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function purchaseDedicatedSubscriptionPlan(planId, token, invoiceRequested = false) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/public/v1/dedicated-subscription-plans/${planId}/purchase`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'X-Replayer-Invoice-Requested': invoiceRequested ? 'true' : 'false'
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function changePlayerSubscription(subscription, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/player/subscription/${subscription.id}`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(subscription)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function changeStorageType(newStorageType, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/players/storage_type`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(newStorageType)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function renewPlayerSubscription(subscriptionId, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/player/subscription/${subscriptionId}/renew`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getRtvVideos(token, partnerUid, rtvChannelId = "all", eventId = null) {
  try {
    const params = eventId === null ? '' : `?${queryString.stringify({eventId})}`;
    const res = await fetch(`${Configuration.apiEndPoint}/partners/${partnerUid}/rtv/${rtvChannelId}${params}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    json.sort(function(a,b){
      return new Date(b.timestamp) - new Date(a.timestamp);
    });
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getRtvPartnerVideos(token, partnerUid, rtvChannelId = "all") {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/partners/${partnerUid}/rtv_video_partner/${rtvChannelId}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    json.sort(function(a,b){
      return new Date(b.timestamp) - new Date(a.timestamp);
    });
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getRtvVideosSoon(token, partnerUid, rtvChannelId = "all", limit = null, offset = null, eventId = null) {
  try {
    const params = Object.fromEntries(Object.entries({limit, offset, eventId}).filter(([_, v]) => v !== null));

    const res = await fetch(`${Configuration.apiEndPoint}/partners/${partnerUid}/rtv_soon/${rtvChannelId}?${queryString.stringify(params)}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function deleteRtvVideo(slotId, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/partners/rtv_video/${slotId}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getVideosStore(token, partnerId, fieldId = null, serviceId = null, fromDatetime = null, toDatetime = null) {
  try {
    const params = Object.fromEntries(Object.entries({fieldId, serviceId, fromDatetime, toDatetime}).filter(([_, v]) => v !== null));

    const res = await fetch(`${Configuration.apiEndPoint}/store_videos/${partnerId}?${queryString.stringify(params)}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getVideosStoreSoon(token, partnerId, fieldId = null, serviceId = null, fromDatetime = null, toDatetime = null) {
  try {
    const params = Object.fromEntries(Object.entries({fieldId, serviceId, fromDatetime, toDatetime}).filter(([_, v]) => v !== null));

    const res = await fetch(`${Configuration.apiEndPoint}/store_videos_soon/${partnerId}?${queryString.stringify(params)}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getVideosStorePartnerVideo(token, partnerId) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/store_videos/video_partner/${partnerId}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function updatePartnerByAdmin(partner, token) {
  try {
    const payload = Object.assign({}, {
      rtvKeepVideos: partner.rtvKeepVideos
    });

    const res = await fetch(`${Configuration.apiEndPoint}/admin/partners/${partner.uid}`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });

    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function updatePartnerDedicatedPageByAdmin(partnerId, values, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/admin/partners/${partnerId}/dedicated_page`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(values)
    });

    const json = await res.json().catch(() => ({}));
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function updatePartnerVideoCostByAdmin(partner, token) {
  try {
    const payload = Object.assign({}, {
      videoPartnerCost: partner.videoPartnerCost
    });

    const res = await fetch(`${Configuration.apiEndPoint}/admin/partners/video_partner/${partner.uid}`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });

    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}


export async function changeStoragePrices(values, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/admin/storage_prices`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(values)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function updatePartnerFieldByAdmin(field, token) {
  try {
    const payload = Object.assign({}, {
      partnerCalibrationEnabled: !!field.partnerCalibrationEnabled,
      rtv: field.rtv,
      recording: field.recording,
      videoHourPrice: Number(field.videoHourPrice) * 100,
      payment: field.payment,
      notifications: field.notifications
    });

    const res = await fetch(`${Configuration.apiEndPoint}/admin/partners/fields/${field.id}`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });

    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function updatePartnerServiceByAdmin(service, token) {
  try {
    const payload = Object.assign({}, {
      rtv: service.rtv,
      recording: service.recording,
      videoHourPrice: Number(service.videoHourPrice) * 100,
      payment: service.payment
    });

    const res = await fetch(`${Configuration.apiEndPoint}/admin/partners/services/${service.id}`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });

    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getStoragePrices(token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/admin/storage_prices`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}


export async function updateUserLanguage(language, token) {
  try {
    const payload = Object.assign({}, {
      language: language
    });
    const res = await fetch(`${Configuration.apiEndPoint}/users/language`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getEvents(token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/partners/events`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getUserTags(token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/tags/user_tags`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function savePartnerEvent(eventData, token) {
  try {
    const payload = Object.assign({}, {
      title: eventData.title,
      description: eventData.description,
      startDatetime: eventData.startDatetime,
      endDatetime: eventData.endDatetime,
      contentStartDatetime: eventData.contentStartDatetime || eventData.startDatetime,
      contentEndDatetime: eventData.contentEndDatetime || eventData.endDatetime,
      rtvChannelId: Number(eventData.rtvChannelId),
      price: Number(eventData.price)
    });

    const res = await fetch(`${Configuration.apiEndPoint}/partners/events`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function saveUserTag(userTag, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/tags/user_tag`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(userTag)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function updatePartnerEvent(eventData, token) {
  try {
    const payload = Object.assign({}, {
      title: eventData.title,
      description: eventData.description,
      startDatetime: eventData.startDatetime,
      endDatetime: eventData.endDatetime,
      contentStartDatetime: eventData.contentStartDatetime || eventData.startDatetime,
      contentEndDatetime: eventData.contentEndDatetime || eventData.endDatetime,
      rtvChannelId: Number(eventData.rtvChannelId),
      price: Number(eventData.price)
    });

    const res = await fetch(`${Configuration.apiEndPoint}/partners/events/${eventData.id}`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function uploadPartnerEventCover(eventId, file, token) {
  try {
    const formData = new FormData();
    formData.append('filepond', file, file.name);
    formData.append('image_type', 'event');

    const res = await fetch(`${Configuration.apiEndPoint}/partners/events/${eventId}/images`, {
      method: 'POST',
      headers: {Authorization: `Bearer ${token}`},
      body: formData
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function updateUserTag(userTag, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/tags/user_tag/${userTag.id}`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(userTag)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getPartnerEvents(partnerId, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/partners/${partnerId}/events`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function deletePartnerEvent(token, eventId) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/partners/events/${eventId}`, {
      method: 'DELETE',
      headers: {Authorization: `Bearer ${token}`}
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function deleteUserTag(token, userTagId) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/tags/user_tag/${userTagId}`, {
      method: 'DELETE',
      headers: {Authorization: `Bearer ${token}`}
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function deleteEventImageFile(token, file, eventId) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/partners/events/${eventId}/images`, {
      method: 'DELETE',
      headers: {Authorization: `Bearer ${token}`},
      body: JSON.stringify({filenames: [file]})
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getUserVideos(tags, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/tags/user_videos?${queryString.stringify({tags})}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getSponsors(token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/sponsors`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getNvrStatus(token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/admin/nvr_status`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

async function adminNvrConfigurationRequest(path, token, method = 'GET', body = null) {
  try {
    const options = {
      method,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    };
    if (body !== null) {
      options.body = JSON.stringify(body);
    }
    const res = await fetch(`${Configuration.apiEndPoint}${path}`, options);
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export const adminListNvrDevices = token =>
  adminNvrConfigurationRequest('/nvr/admin/devices', token);

export const getSlotMediaStatus = (slotId, token) =>
  adminNvrConfigurationRequest(`/slots/${encodeURIComponent(slotId)}/media-status`, token);

export const getSlotScheduleUpdate = (slotId, commandId, token) =>
  adminNvrConfigurationRequest(`/slots/${encodeURIComponent(slotId)}/schedule-updates/${encodeURIComponent(commandId)}`, token);

export const getCalendarMediaStatus = (slotIds, token) =>
  adminNvrConfigurationRequest(`/slots/media-status?ids=${slotIds.join(',')}`, token);

export const adminListNvrConfigurationRevisions = (deviceUid, token) =>
  adminNvrConfigurationRequest(`/nvr/admin/devices/${encodeURIComponent(deviceUid)}/configuration-revisions`, token);

export const adminGetNvrConfigurationRevision = (deviceUid, revision, token) =>
  adminNvrConfigurationRequest(`/nvr/admin/devices/${encodeURIComponent(deviceUid)}/configuration-revisions/${revision}`, token);

export const adminValidateNvrConfiguration = (deviceUid, configuration, token) =>
  adminNvrConfigurationRequest(`/nvr/admin/devices/${encodeURIComponent(deviceUid)}/configuration-validation`, token, 'POST', configuration);

export const adminPublishNvrConfiguration = (deviceUid, configuration, token) =>
  adminNvrConfigurationRequest(`/nvr/admin/devices/${encodeURIComponent(deviceUid)}/configuration-revisions`, token, 'POST', configuration);

export const adminRollbackNvrConfiguration = (deviceUid, revision, token) =>
  adminNvrConfigurationRequest(`/nvr/admin/devices/${encodeURIComponent(deviceUid)}/configuration-rollback`, token, 'POST', {revision});

export const adminListNvrSecretRevisions = (deviceUid, token) =>
  adminNvrConfigurationRequest(`/nvr/admin/devices/${encodeURIComponent(deviceUid)}/secret-revisions`, token);

export const adminGetNvrSecretRevision = (deviceUid, revision, token) =>
  adminNvrConfigurationRequest(`/nvr/admin/devices/${encodeURIComponent(deviceUid)}/secret-revisions/${revision}`, token);

export const adminValidateNvrSecrets = (deviceUid, secretsDocument, token) =>
  adminNvrConfigurationRequest(`/nvr/admin/devices/${encodeURIComponent(deviceUid)}/secret-validation`, token, 'POST', secretsDocument);

export const adminPublishNvrSecrets = (deviceUid, secretsDocument, token) =>
  adminNvrConfigurationRequest(`/nvr/admin/devices/${encodeURIComponent(deviceUid)}/secret-revisions`, token, 'POST', secretsDocument);

export const adminRollbackNvrSecrets = (deviceUid, revision, token) =>
  adminNvrConfigurationRequest(`/nvr/admin/devices/${encodeURIComponent(deviceUid)}/secret-rollback`, token, 'POST', {revision});

export async function getUserVideoTags(videoId, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/tags/user_video_tags/${videoId}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function saveSponsor(sponsor, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/sponsors`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(sponsor)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function saveUserVideoTag(userVideoTag, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/tags/user_video_tag`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(userVideoTag)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function updateSponsor(sponsor, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/sponsors/${sponsor.id}`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(sponsor)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function updateUserVideoTag(userVideoTag, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/tags/user_video_tag/${userVideoTag.id}`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(userVideoTag)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function deleteSponsor(token, sponsorId) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/sponsors/${sponsorId}`, {
      method: 'DELETE',
      headers: {Authorization: `Bearer ${token}`}
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function deleteUserVideoTag(token, userVideoTagId) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/tags/user_video_tag/${userVideoTagId}`, {
      method: 'DELETE',
      headers: {Authorization: `Bearer ${token}`}
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function deleteSponsorImageFile(token, file, sponsorId) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/sponsors/${sponsorId}/image`, {
      method: 'DELETE',
      headers: {Authorization: `Bearer ${token}`},
      body: JSON.stringify({filenames: [file]})
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getSponsor(token, sponsorId) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/sponsors/${sponsorId}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getPartnerRtvChannels(partnerId, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/partners/${partnerId}/rtv_channels`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    let json = await res.json();
    json = json.map(r => {
      r.rtvPrice /= 100;
      r.rtvPriceMembership /= 100;
      r.rtvPriceEvent /= 100;
      r.dedicatedPlans = (r.dedicatedPlans || []).map(plan => ({
        ...plan,
        price: plan.price / 100
      }));
      return r;
    })
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

const dedicatedPlansPayload = plans => (plans || []).map(plan => ({
  months: plan.months,
  price: Math.round(Number(plan.price || 0) * 100),
  active: Boolean(plan.active),
  label: plan.label || null,
  promotionEndsAt: plan.promotionEndsAt || null,
  autoRenew: Boolean(plan.autoRenew)
}));

const partnerRtvChannelPayload = rtvChannel => ({
  uid: rtvChannel.uid,
  name: rtvChannel.name,
  rtvPrice: Math.round(Number(rtvChannel.rtvPrice || 0) * 100),
  rtvKeepVideos: rtvChannel.rtvKeepVideos,
  rtvPriceMembership: Math.round(Number(rtvChannel.rtvPriceMembership || 0) * 100),
  rtvPriceEvent: Math.round(Number(rtvChannel.rtvPriceEvent || 0) * 100),
  blocked: Boolean(rtvChannel.blocked),
  type: rtvChannel.type,
  dedicatedPlans: dedicatedPlansPayload(rtvChannel.dedicatedPlans)
});

export async function createPartnerRtvChannel(rtvChannel, token) {
  try {
    const payload = partnerRtvChannelPayload(rtvChannel);

    const res = await fetch(`${Configuration.apiEndPoint}/admin/partners/rtv_channels`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function updatePartnerRtvChannel(rtvChannel, token) {
  try {
    const payload = partnerRtvChannelPayload(rtvChannel);

    const res = await fetch(`${Configuration.apiEndPoint}/admin/partners/rtv_channels/${rtvChannel.id}`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function deleteRtvChannel(rtvChannelId, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/admin/partners/rtv_channels/${rtvChannelId}`, {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function deleteRtvChannelImageFile(token, file, rtvChannelId) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/partners/rtv_channels/${rtvChannelId}/images`, {
      method: 'DELETE',
      headers: {Authorization: `Bearer ${token}`},
      body: JSON.stringify({filenames: [file]})
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function saveXml(data, token) {
  const formData = new FormData();
  formData.append('xml', data.selectedFile);
  formData.append('fieldId', data.fieldId);
  formData.append('xmlType', data.xmlType);

  try {
    const res = await fetch(`${Configuration.apiEndPoint}/xml`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`
      },
      body: formData,
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function savePartnerVideoXml(data, token) {
  const formData = new FormData();
  formData.append('xml', data.selectedFile);
  formData.append('partnerId', data.partnerId);
  formData.append('xmlType', data.xmlType);

  try {
    const res = await fetch(`${Configuration.apiEndPoint}/xml/partner`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`
      },
      body: formData,
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function createCamera(values, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/admin/camera`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(values)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function modifyCamera(values, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/admin/camera/${values.id}`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(values)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function deleteCamera(cameraId, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/admin/camera/${cameraId}`, {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getFavouriteRtvChannels(token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/players/get_favourite_rtv_channels`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function addRtvChannelToFavourite(rtv_channel_id, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/players/add_favourite_rtv_channel`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({rtv_channel_id})
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function removeRtvChannelFromFavourite(rtv_channel_id, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/players/remove_favourite_rtv_channel/${rtv_channel_id}`, {
      method: 'DELETE',
      headers: {Authorization: `Bearer ${token}`}
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function createPartnerVideo(data, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/video_partners`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(data)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function editPartnerVideo(videoId, data, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/video_partners/${videoId}`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(data)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getPartnerVideos(token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/video_partners/all`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function deletePartnerVideo(videoId, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/video_partners/${videoId}`, {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function deleteVideoImageFile(token, file, videoPartnerId) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/video_partners/${videoPartnerId}/images`, {
      method: 'DELETE',
      headers: {Authorization: `Bearer ${token}`},
      body: JSON.stringify({filenames: [file]})
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function changePartnerPermissions(values, partnerId, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/admin/change_partner_permissions/${partnerId}`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(values)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function postUsageData(usageType, partnerId, rtvChannelId, slotId, streaming, contextPage, videoPartnerTitle, videoPartnerName, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/players/usage_data`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({usageType, partnerId, rtvChannelId, slotId, streaming, contextPage, videoPartnerTitle, videoPartnerName})
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function adminCreateCategory(values, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/admin/categories`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(values)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getRtvCategories(token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/admin/categories`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function adminEditCategory(values, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/admin/categories/${values.id}`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(values)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function adminAddOrRemoveChannelFromCategory(rtvChannelId, add, categoryId, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/admin/categories/channel_to_category/${categoryId}`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({rtvChannelId, add, categoryId})
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function adminDeleteCategory(categoryId, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/admin/categories/${categoryId}`, {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function deleteCategoryImageFile(token, file, categoryId) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/admin/categories/image/${categoryId}`, {
      method: 'DELETE',
      headers: {Authorization: `Bearer ${token}`},
      body: JSON.stringify({filenames: [file]})
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function playerGetRtvCategories(token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/players/categories`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function partnerGetRtvCategories(token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/partners/categories`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function downloadNvrTracking(filters, token) {
  const downloadFile = (url, filename, slotId) => {
    if (slotId === null){
      fetch(url)
        .then(response => response.blob())
        .then(blob => {
          const link = document.createElement("a");
          link.href = URL.createObjectURL(blob);
          link.download = filename;
          link.click();
      })
      .catch(console.error);
    }
    else{
      fetch(url)
        .then(response => response.blob())
        .then(blob => blob.text())  // Convert the Blob into a string
        .then(csvString => {
          // Parse the CSV string into rows
          const rows = csvString.split('\n').map(row => row.split(','));

          // Filter the rows based on the value of a column
          const filteredRows = rows.filter(row => String(row[0]).trim() === String(slotId).trim() || String(row[0]).trim() === "slot_id");

          // Convert the filtered rows back into a CSV string
          const filteredCsvString = filteredRows.map(row => row.join(',')).join('\n');

          // Convert the CSV string back into a Blob
          const filteredBlob = new Blob([filteredCsvString], {type: 'text/csv'});

          // Download the filtered CSV
          const link = document.createElement("a");
          link.href = URL.createObjectURL(filteredBlob);
          link.download = filename;
          link.click();
      })
      .catch(console.error);
    }
  };

  try {
    const fileName = filters.slotId === null ? `ReplayerNvrTracking_${new Date(filters.date).toISOString().slice(0,10)}.csv` : `ReplayerNvrTracking_${new Date(filters.date).toISOString().slice(0,10)}_id${filters.slotId}.csv`;
    const res = await fetch(`${Configuration.apiEndPoint}/admin/nvr_report?date=${filters.date ? new Date(filters.date).toISOString().slice(0,10) : ''}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    if (res.status === 200) {
      const json = await res.json();
      downloadFile(json, fileName, filters.slotId);
    }

    return {status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function downloadSliceVideosReport(filters, token) {
  try {
    const filename = `ConcatVideoReport-${new Date(filters.fromDatetime).toISOString().slice(0,10)}-${new Date(filters.toDatetime).toISOString().slice(0,10)}.xls`;
    const res = await fetch(`${Configuration.apiEndPoint}/admin/concat_slice_report?fromDatetime=${filters.fromDatetime ? new Date(filters.fromDatetime).toISOString().slice(0,10) : ''}&toDatetime=${filters.toDatetime ? new Date(filters.toDatetime).toISOString().slice(0,10) : ''}&slotId=${filters.slotId ? filters.slotId : ''}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'Content-Disposition': 'attachment; filename=report.xls'
      }
    });
    if (res.status === 200) {
      const blob = await res.blob();
      download(blob, filename);
      return {status: res.status};
    } else {
      const json = await res.json();
      return {json, status: res.status};
    }
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function downloadDeleteVideoReport(filters, token) {
  const downloadFile = (url, filename, slotId) => {
    if (slotId === null){
      fetch(url)
        .then(response => response.blob())
        .then(blob => {
          const link = document.createElement("a");
          link.href = URL.createObjectURL(blob);
          link.download = filename;
          link.click();
      })
      .catch(console.error);
    }
    else{
      fetch(url)
        .then(response => response.blob())
        .then(blob => blob.text())  // Convert the Blob into a string
        .then(csvString => {
          // Parse the CSV string into rows
          const rows = csvString.split('\n').map(row => row.split(','));

          // Filter the rows based on the value of a column
          const filteredRows = rows.filter(row => String(row[0]).trim() === String(slotId).trim() || String(row[0]).trim() === "slot_id");

          // Convert the filtered rows back into a CSV string
          const filteredCsvString = filteredRows.map(row => row.join(',')).join('\n');

          // Convert the CSV string back into a Blob
          const filteredBlob = new Blob([filteredCsvString], {type: 'text/csv'});

          // Download the filtered CSV
          const link = document.createElement("a");
          link.href = URL.createObjectURL(filteredBlob);
          link.download = filename;
          link.click();
      })
      .catch(console.error);
    }
  };

  try {
    const fileName = filters.slotId === null ? `DeleteVideoReport_${new Date(filters.date).toISOString().slice(0,10)}.csv` : `DeleteVideoReport_${new Date(filters.date).toISOString().slice(0,10)}_id${filters.slotId}.csv`;
    const res = await fetch(`${Configuration.apiEndPoint}/admin/deleted_report?date=${filters.date ? new Date(filters.date).toISOString().slice(0,10) : ''}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    if (res.status === 200) {
      const json = await res.json();
      downloadFile(json, fileName, filters.slotId);
    }

    return {status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function partnerCreateFixedCost(values, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/partners/fields/costs`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(values)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function partnerDeleteFixedCost(id, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/partners/fields/costs/${id}`, {
      method: 'DELETE',
      headers: {Authorization: `Bearer ${token}`},
      body: JSON.stringify({id: id})
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function saveEditedPlayers(bookingData, bookingId, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/bookings/players/${bookingId}`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(bookingData)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getLiveVideos(token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/players/streaming_videos`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });

    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}}
  }
}

export async function getComingSoonVideos(token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/players/streaming_soon_videos`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });

    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}}
  }
}

export async function partnerGetAffiliations(token, partnerId) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/affiliations/partners/${partnerId}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });

    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}}
  }
}

export async function editAffiliation(affiliation, token) {
  var data = {...affiliation};
  delete data.fields;
  delete data.services;
  delete data.players;
  delete data.type;
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/affiliations/${affiliation.affiliationId}`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(data)
    });

    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}}
  }
}

export async function deleteAffiliation(affiliationId, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/affiliations/${affiliationId}`, {
      method: 'DELETE',
      headers: {Authorization: `Bearer ${token}`}
    });

    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}}
  }
}

export async function createAffiliation(affiliation, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/affiliations`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(affiliation)
    });

    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}}
  }
}

export async function activateDeactivateAffiliation(affiliationId, active, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/affiliations/activate/${affiliationId}`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({active})
    });

    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}}
  }
}

export async function getAffiliatedPlayers(token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/affiliations/players`, {
      method: 'GET',
      headers: {Authorization: `Bearer ${token}`}
    });

    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}}
  }
}

export async function deletePlayerFromAffiliation(playerId, affiliationId, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/affiliations/players`, {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({'playersAffiliations': [{playerId, affiliationId}]})
    });

    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}}
  }
}

export async function editAffiliationPlayer(affiliationId, playerId, active, medicalCertificateTimestamp, payed, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/affiliations/players`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({'playersAffiliations': [{affiliationId, playerId, active, medicalCertificateTimestamp, payed}]})
    });

    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}}
  }
}

export async function getPlayerAffiliations(token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/affiliations/get_player_affiliations`, {
      method: 'GET',
      headers: {Authorization: `Bearer ${token}`}
    });

    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}}
  }
}

export async function requestAffiliation(affiliationId, playerId, playerOnlinePayment, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/affiliations/players`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({"playersAffiliations":[{affiliationId, playerId, playerOnlinePayment}]})
    });

    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}}
  }
}

export async function renewPlayerAffiliation(playerId, affiliationId, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/affiliations/renew`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({"playersAffiliations":[{affiliationId, playerId}]})
    });

    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}}
  }
}

export async function renewAffiliation(playerId, affiliationId, onlinePayment, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/affiliations/renew_player`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({affiliationId, playerId, onlinePayment})
    });

    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}}
  }
}

export async function getAffiliationRenewRequests(token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/affiliations/renew_player`, {
      method: 'GET',
      headers: {Authorization: `Bearer ${token}`}
    });

    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}}
  }
}

export async function getRankingVerificationRequests(token, status = 'pending') {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/affiliations/ranking_verification_requests?status=${encodeURIComponent(status)}`, {
      method: 'GET',
      headers: {Authorization: `Bearer ${token}`}
    });

    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}}
  }
}

export async function reviewRankingVerificationRequest(requestId, payload, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/affiliations/ranking_verification_requests/${requestId}`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });

    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}}
  }
}

export async function setAffiliatedPlayerRanking(playerId, sport, payload, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/affiliations/players/${playerId}/rankings/${encodeURIComponent(sport)}`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });
    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function acceptOrRefuseAffiliationRenewRequest(request, accept, token) {
  try {
    const payload = {
      'affiliationId': request.affiliationId,
      'playerId': request.playerId,
      'requestId': request.requestId,
      'approve': accept
    }
    const res = await fetch(`${Configuration.apiEndPoint}/affiliations/renew_player`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({'renewAffiliationsRequests': [payload]})
    });

    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}}
  }
}

export async function enableDisableNotifications(nvrId, notificationsEnabled, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/admin/nvr_notification/${nvrId}`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({notificationsEnabled})
    });

    const json = await res.json();
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}}
  }
}

export async function getExpirationOptions(token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/admin/expiration_options`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` }
    });

    const json = await res.json();
    return { json, status: res.status };
  } catch (error) {
    return { status: -1, json: { error: error.message } };
  }
}

export async function getAdvertisementTypes(token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/admin/advertisement_types`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` }
    });

    const json = await res.json();
    return { json, status: res.status };
  } catch (error) {
    return { status: -1, json: { error: error.message } };
  }
}

export async function getCameraModes(token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/admin/camera_modes`, {
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    const json = await res.json().catch(() => ({}));
    return { status: res.status, json };
  } catch (e) {
    return { status: -1, json: { error: e.message } };
  }
}

export async function getCameraClasses(token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/admin/camera_classes`, {
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    const json = await res.json().catch(() => ({}));
    return { status: res.status, json };
  } catch (e) {
    return { status: -1, json: { error: e.message } };
  }
}


// ==== Admin Settings: Expiration Options ====
export async function createExpirationOption(values, token) {
  try {
    const payload = {
      name: (values.name || '').trim(),
      description: values.description && String(values.description).trim() !== '' ? values.description : null,
      duration: Number(values.duration)
    };
    const res = await fetch(`${Configuration.apiEndPoint}/admin/expiration_options`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload)
    });
    const json = await res.json(); return { json, status: res.status };
  } catch (error) { return { status: -1, json: { error: error.message } }; }
}

export async function updateExpirationOption(values, token) {
  try {
    const payload = {
      name: (values.name || '').trim(),
      description: values.description && String(values.description).trim() !== '' ? values.description : null,
      duration: Number(values.duration)
    };
    const res = await fetch(`${Configuration.apiEndPoint}/admin/expiration_options/${values.id}`, {
      method: 'PUT',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload)
    });
    const json = await res.json(); return { json, status: res.status };
  } catch (error) { return { status: -1, json: { error: error.message } }; }
}

export async function deleteExpirationOption(id, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/admin/expiration_options/${id}`, {
      method: 'DELETE',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
    });
    const json = await res.json().catch(()=> ({})); return { json, status: res.status };
  } catch (error) { return { status: -1, json: { error: error.message } }; }
}

// ==== Admin Settings: Advertisement Types ====
export async function createAdvertisementType(values, token) {
  try {
    const payload = {
      name: (values.name || '').trim(),
      description: values.description && String(values.description).trim() !== '' ? values.description : null,
      xml: values.xml && String(values.xml).trim() !== '' ? values.xml : null,
      flat: values.flat && String(values.flat).trim() !== '' ? values.flat : null,
      pano: values.pano && String(values.pano).trim() !== '' ? values.pano : null
    };
    const res = await fetch(`${Configuration.apiEndPoint}/admin/advertisement_types`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload)
    });
    const json = await res.json(); return { json, status: res.status };
  } catch (error) { return { status: -1, json: { error: error.message } }; }
}

export async function updateAdvertisementType(values, token) {
  try {
    const payload = {
      name: (values.name || '').trim(),
      description: values.description && String(values.description).trim() !== '' ? values.description : null,
      xml: values.xml && String(values.xml).trim() !== '' ? values.xml : null,
      flat: values.flat && String(values.flat).trim() !== '' ? values.flat : null,
      pano: values.pano && String(values.pano).trim() !== '' ? values.pano : null
    };
    const res = await fetch(`${Configuration.apiEndPoint}/admin/advertisement_types/${values.id}`, {
      method: 'PUT',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload)
    });
    const json = await res.json(); return { json, status: res.status };
  } catch (error) { return { status: -1, json: { error: error.message } }; }
}

export async function deleteAdvertisementType(id, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/admin/advertisement_types/${id}`, {
      method: 'DELETE',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
    });
    const json = await res.json().catch(()=> ({})); return { json, status: res.status };
  } catch (error) { return { status: -1, json: { error: error.message } }; }
}

// ==== Admin Shop ====
const shopScopeQuery = options => {
  const params = new URLSearchParams();
  if (options && options.partnerId !== undefined && options.partnerId !== null && options.partnerId !== '') {
    params.set('partner_id', options.partnerId);
  }
  if (options && options.allPartners) {
    params.set('partner_id', 'all');
  }
  if (options && options.status) {
    params.set('status', options.status);
  }
  if (options && options.partnerSlug) {
    params.set('partner_slug', options.partnerSlug);
  }
  const query = params.toString();
  return query ? `?${query}` : '';
};

async function adminShopRequest(token, path, method = 'GET', body = null) {
  try {
    const options = {
      method,
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`
      }
    };
    if (body !== null) {
      options.headers['Content-Type'] = 'application/json';
      options.body = JSON.stringify(body);
    }
    const res = await fetch(`${Configuration.apiEndPoint}${path}`, options);
    const json = await res.json().catch(() => ({}));
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

async function partnerShopRequest(token, path, method = 'GET', body = null) {
  try {
    const options = {
      method,
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`
      }
    };
    if (body !== null) {
      options.headers['Content-Type'] = 'application/json';
      options.body = JSON.stringify(body);
    }
    const res = await fetch(`${Configuration.apiEndPoint}${path}`, options);
    const json = await res.json().catch(() => ({}));
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export const getAdminShopProducts = (token, options = {}) =>
  adminShopRequest(token, `/admin/shop/products${shopScopeQuery(options)}`);

export const getPartnerShopProducts = token =>
  partnerShopRequest(token, '/partner/shop/products');

export const createAdminShopProduct = (token, product, options = {}) =>
  adminShopRequest(token, `/admin/shop/products${shopScopeQuery(options)}`, 'POST', product);

export const createPartnerShopProduct = (token, product) =>
  partnerShopRequest(token, '/partner/shop/products', 'POST', product);

export const updateAdminShopProduct = (token, productId, product, options = {}) =>
  adminShopRequest(token, `/admin/shop/products/${productId}${shopScopeQuery(options)}`, 'PUT', product);

export const updatePartnerShopProduct = (token, productId, product) =>
  partnerShopRequest(token, `/partner/shop/products/${productId}`, 'PUT', product);

export const deleteAdminShopProduct = (token, productId, options = {}) =>
  adminShopRequest(token, `/admin/shop/products/${productId}${shopScopeQuery(options)}`, 'DELETE');

export const deletePartnerShopProduct = (token, productId) =>
  partnerShopRequest(token, `/partner/shop/products/${productId}`, 'DELETE');

export const createAdminShopVariant = (token, productId, variant, options = {}) =>
  adminShopRequest(token, `/admin/shop/products/${productId}/variants${shopScopeQuery(options)}`, 'POST', variant);

export const createPartnerShopVariant = (token, productId, variant) =>
  partnerShopRequest(token, `/partner/shop/products/${productId}/variants`, 'POST', variant);

export const updateAdminShopVariant = (token, variantId, variant, options = {}) =>
  adminShopRequest(token, `/admin/shop/variants/${variantId}${shopScopeQuery(options)}`, 'PUT', variant);

export const updatePartnerShopVariant = (token, variantId, variant) =>
  partnerShopRequest(token, `/partner/shop/variants/${variantId}`, 'PUT', variant);

export const deleteAdminShopVariant = (token, variantId, options = {}) =>
  adminShopRequest(token, `/admin/shop/variants/${variantId}${shopScopeQuery(options)}`, 'DELETE');

export const deletePartnerShopVariant = (token, variantId) =>
  partnerShopRequest(token, `/partner/shop/variants/${variantId}`, 'DELETE');

export async function uploadAdminShopProductImages(token, productId, images, label = '', variantId = null, options = {}) {
  try {
    const formData = new FormData();
    images.forEach(image => formData.append('images', image));
    if (label) {
      formData.append('label', label);
    }
    if (variantId !== null && variantId !== '') {
      formData.append('variant_id', variantId);
    }
    const res = await fetch(`${Configuration.apiEndPoint}/admin/shop/products/${productId}/images${shopScopeQuery(options)}`, {
      method: 'POST',
      headers: {Authorization: `Bearer ${token}`},
      body: formData
    });
    const json = await res.json().catch(() => ({}));
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function uploadPartnerShopProductImages(token, productId, images, label = '', variantId = null) {
  try {
    const formData = new FormData();
    images.forEach(image => formData.append('images', image));
    if (label) {
      formData.append('label', label);
    }
    if (variantId !== null && variantId !== '') {
      formData.append('variant_id', variantId);
    }
    const res = await fetch(`${Configuration.apiEndPoint}/partner/shop/products/${productId}/images`, {
      method: 'POST',
      headers: {Authorization: `Bearer ${token}`},
      body: formData
    });
    const json = await res.json().catch(() => ({}));
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export const deleteAdminShopProductImage = (token, productId, imageId, options = {}) =>
  adminShopRequest(token, `/admin/shop/products/${productId}/images/${imageId}${shopScopeQuery(options)}`, 'DELETE');

export const deletePartnerShopProductImage = (token, productId, imageId) =>
  partnerShopRequest(token, `/partner/shop/products/${productId}/images/${imageId}`, 'DELETE');

export const getAdminShopOrders = (token, status = '', options = {}) =>
  adminShopRequest(token, `/admin/shop/orders${shopScopeQuery({...options, status})}`);

export const getPartnerShopOrders = (token, status = '') =>
  partnerShopRequest(token, `/partner/shop/orders${shopScopeQuery({status})}`);

export const updateAdminShopOrder = (token, orderId, order, options = {}) =>
  adminShopRequest(token, `/admin/shop/orders/${orderId}${shopScopeQuery(options)}`, 'PUT', order);

export const updatePartnerShopOrder = (token, orderId, order) =>
  partnerShopRequest(token, `/partner/shop/orders/${orderId}`, 'PUT', order);

export const getAdminShopSettings = (token, options = {}) =>
  adminShopRequest(token, `/admin/shop/settings${shopScopeQuery(options)}`);

export const updateAdminShopSettings = (token, settings, options = {}) =>
  adminShopRequest(token, `/admin/shop/settings${shopScopeQuery(options)}`, 'PUT', settings);

export const getPartnerShopSettings = token =>
  partnerShopRequest(token, '/partner/shop/settings');

export const updatePartnerShopSettings = (token, settings) =>
  partnerShopRequest(token, '/partner/shop/settings', 'PUT', settings);

export async function getPublicShopProducts(options = {}) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/shop/products${shopScopeQuery(options)}`, {
      method: 'GET',
      headers: {Accept: 'application/json'}
    });
    const json = await res.json().catch(() => ({}));
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getPublicShopSettings(options = {}) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/shop/settings${shopScopeQuery(options)}`, {
      method: 'GET',
      headers: {Accept: 'application/json'}
    });
    const json = await res.json().catch(() => ({}));
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function purchaseShopProduct(token, variantId, quantity) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/shop/purchase`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({variant_id: variantId, quantity})
    });
    const json = await res.json().catch(() => ({}));
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function purchaseShopOrder(token, items, delivery, options = {}) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/shop/orders`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'X-Replayer-Invoice-Requested': options.invoiceRequested ? 'true' : 'false'
      },
      body: JSON.stringify({
        items,
        delivery,
        delivery_method: options.deliveryMethod || 'shipping',
        partner_id: options.partnerId,
        partner_slug: options.partnerSlug
      })
    });
    const json = await res.json().catch(() => ({}));
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getAdminInvoiceRequests(token, filters = {}) {
  try {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.email) params.append('email', filters.email);
    const query = params.toString();
    const res = await fetch(`${Configuration.apiEndPoint}/admin/invoice-requests${query ? `?${query}` : ''}`, {
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json().catch(() => ({}));
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function updateAdminInvoiceRequest(token, transactionId, invoiceStatus) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/admin/invoice-requests/${transactionId}`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({invoiceStatus})
    });
    const json = await res.json().catch(() => ({}));
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

// ==== Admin Settings: Camera Modes ====
export async function createCameraMode(values, token) {
  try {
    const payload = {
      name: (values.name || '').trim(),
      description: values.description && String(values.description).trim() !== '' ? values.description : null
    };
    const res = await fetch(`${Configuration.apiEndPoint}/admin/camera_modes`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload)
    });
    const json = await res.json(); return { json, status: res.status };
  } catch (error) { return { status: -1, json: { error: error.message } }; }
}

export async function updateCameraMode(values, token) {
  try {
    const payload = {
      name: (values.name || '').trim(),
      description: values.description && String(values.description).trim() !== '' ? values.description : null
    };
    const res = await fetch(`${Configuration.apiEndPoint}/admin/camera_modes/${values.id}`, {
      method: 'PUT',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload)
    });
    const json = await res.json(); return { json, status: res.status };
  } catch (error) { return { status: -1, json: { error: error.message } }; }
}

export async function deleteCameraMode(id, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/admin/camera_modes/${id}`, {
      method: 'DELETE',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
    });
    const json = await res.json().catch(()=> ({})); return { json, status: res.status };
  } catch (error) { return { status: -1, json: { error: error.message } }; }
}

// ==== Admin Settings: Camera Classes ====
export async function createCameraClass(values, token) {
  try {
    const payload = {
      name: (values.name || '').trim(),
      description: values.description && String(values.description).trim() !== '' ? values.description : null
    };
    const res = await fetch(`${Configuration.apiEndPoint}/admin/camera_classes`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload)
    });
    const json = await res.json(); return { json, status: res.status };
  } catch (error) { return { status: -1, json: { error: error.message } }; }
}

export async function updateCameraClass(values, token) {
  try {
    const payload = {
      name: (values.name || '').trim(),
      description: values.description && String(values.description).trim() !== '' ? values.description : null
    };
    const res = await fetch(`${Configuration.apiEndPoint}/admin/camera_classes/${values.id}`, {
      method: 'PUT',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload)
    });
    const json = await res.json(); return { json, status: res.status };
  } catch (error) { return { status: -1, json: { error: error.message } }; }
}

export async function deleteCameraClass(id, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/admin/camera_classes/${id}`, {
      method: 'DELETE',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
    });
    const json = await res.json().catch(()=> ({})); return { json, status: res.status };
  } catch (error) { return { status: -1, json: { error: error.message } }; }
}

// ==== Public Partner Catalog ====
export async function resolvePublicPartner({slug = null, host = null} = {}) {
  try {
    const params = new URLSearchParams();
    if (slug) {
      params.set('slug', slug);
    }
    if (host) {
      params.set('host', host);
    }

    const res = await fetch(`${Configuration.apiEndPoint}/public/v1/partners/resolve?${params.toString()}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      }
    });
    const json = await res.json().catch(() => ({}));
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getPublicPartnerCatalog(partnerSlug, query = {}, token = null) {
  try {
    const params = new URLSearchParams();
    if (query.types) {
      params.set('types', query.types);
    }
    if (query.limit) {
      params.set('limit', query.limit);
    }
    if (query.cursor) {
      params.set('cursor', query.cursor);
    }
    if (query.q) {
      params.set('q', query.q);
    }
    if (query.from) {
      params.set('from', query.from);
    }
    if (query.to) {
      params.set('to', query.to);
    }
    if (query.channelId) {
      params.set('channel_id', query.channelId);
    }
    if (query.view) {
      params.set('view', query.view);
    }

    const querySuffix = params.toString() ? `?${params.toString()}` : '';
    const headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const res = await fetch(`${Configuration.apiEndPoint}/public/v1/partners/${partnerSlug}/catalog${querySuffix}`, {
      method: 'GET',
      headers
    });

    const json = await res.json().catch(() => ({}));
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getPublicPartnerBookingAvailability(partnerSlug, targetDate = new Date(), mode = 'booking') {
  try {
    const start = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate());
    const end = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate(), 23, 59, 59);
    const params = new URLSearchParams({
      from: start.toISOString(),
      to: end.toISOString(),
      mode
    });
    const res = await fetch(`${Configuration.apiEndPoint}/public/v1/partners/${partnerSlug}/booking-availability?${params.toString()}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      }
    });
    const json = await res.json().catch(() => ({}));
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function postPublicPlayIntent(payload, token = null) {
  try {
    const headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const res = await fetch(`${Configuration.apiEndPoint}/public/v1/play-intent`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload)
    });
    const json = await res.json().catch(() => ({}));
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getVideoAnalysisConfig(contentType, contentId, token) {
  try {
    const params = new URLSearchParams({
      contentType,
      contentId
    });
    const res = await fetch(`${Configuration.apiEndPoint}/video-analysis/config?${params.toString()}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json().catch(() => ({}));
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function createVideoAnalysisRequest(payload, token, invoiceRequested = false) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/video-analysis/requests`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'X-Replayer-Invoice-Requested': invoiceRequested ? 'true' : 'false'
      },
      body: JSON.stringify(payload)
    });
    const json = await res.json().catch(() => ({}));
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getVideoAnalysisRequests(token, status = null) {
  try {
    const params = status ? `?${new URLSearchParams({status}).toString()}` : '';
    const res = await fetch(`${Configuration.apiEndPoint}/video-analysis/requests${params}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json().catch(() => ({}));
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getVideoAnalysisReport(requestId, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/video-analysis/requests/${requestId}/report`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json().catch(() => ({}));
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function shareVideoAnalysisRequest(requestId, sharing, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/video-analysis/requests/${requestId}/sharing`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({sharing})
    });
    const json = await res.json().catch(() => ({}));
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function createVideoAnalysisDownload(requestId, payload, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/video-analysis/requests/${requestId}/downloads`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });
    const json = await res.json().catch(() => ({}));
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getVideoAnalysisDownload(requestId, jobId, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/video-analysis/requests/${requestId}/downloads/${jobId}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json().catch(() => ({}));
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getSharedVideoAnalysisReport(sharingId, token = null) {
  try {
    const headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    const res = await fetch(`${Configuration.apiEndPoint}/video-analysis/sharing/${sharingId}/report`, {
      method: 'GET',
      headers
    });
    const json = await res.json().catch(() => ({}));
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function updateVideoAnalysisPlayers(requestId, players, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/video-analysis/requests/${requestId}/players`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({players})
    });
    const json = await res.json().catch(() => ({}));
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function updateVideoAnalysisSegmentScores(requestId, scores, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/video-analysis/requests/${requestId}/segment-scores`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({scores})
    });
    const json = await res.json().catch(() => ({}));
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function deleteVideoAnalysisRequest(requestId, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/video-analysis/requests/${requestId}`, {
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json().catch(() => ({}));
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function adminGetVideoAnalysisConfigs(token) {
  if (!token) {
    return {status: 401, json: {error: 'missing_token'}};
  }
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/admin/video-analysis/config`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json().catch(() => ({}));
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function adminCreateVideoAnalysisConfig(payload, token) {
  if (!token) {
    return {status: 401, json: {error: 'missing_token'}};
  }
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/admin/video-analysis/config`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });
    const json = await res.json().catch(() => ({}));
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function adminUpdateVideoAnalysisConfig(configId, payload, token) {
  if (!token) {
    return {status: 401, json: {error: 'missing_token'}};
  }
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/admin/video-analysis/config/${configId}`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });
    const json = await res.json().catch(() => ({}));
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function postPublicVideoReadyNotification(slotId, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/public/v1/slots/${slotId}/video-ready-notifications`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json().catch(() => ({}));
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getPublicPartnerAffiliations(partnerSlug, token = null) {
  try {
    const headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const res = await fetch(`${Configuration.apiEndPoint}/public/v1/partners/${partnerSlug}/affiliations`, {
      method: 'GET',
      headers
    });
    const json = await res.json().catch(() => ({}));
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function resumePublicPlayIntent(resumeToken, token) {
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/public/v1/play-intent/resume/${resumeToken}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    const json = await res.json().catch(() => ({}));
    return {json, status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function createRepFeedback(payload, token) {
  if (!token) return {status: 401, json: {error: 'missing_token'}};
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/rep/feedback`, {
      method: 'POST',
      headers: {Accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${token}`},
      body: JSON.stringify(payload)
    });
    return {status: res.status, json: await res.json().catch(() => ({}))};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getRepSettings(token) {
  if (!token) return {status: 401, json: {error: 'missing_token'}};
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/rep/settings`, {
      headers: {Accept: 'application/json', Authorization: `Bearer ${token}`}
    });
    return {status: res.status, json: await res.json().catch(() => ({}))};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function adminGetRepSettings(token) {
  if (!token) return {status: 401, json: {error: 'missing_token'}};
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/admin/rep/settings`, {
      headers: {Accept: 'application/json', Authorization: `Bearer ${token}`}
    });
    return {status: res.status, json: await res.json().catch(() => ({}))};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function adminUpdateRepSettings(enabled, token) {
  if (!token) return {status: 401, json: {error: 'missing_token'}};
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/admin/rep/settings`, {
      method: 'PUT',
      headers: {Accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${token}`},
      body: JSON.stringify({enabled})
    });
    return {status: res.status, json: await res.json().catch(() => ({}))};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function trackRepMetric(payload, token) {
  if (!token) return {status: 401, json: {error: 'missing_token'}};
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/rep/metrics`, {
      method: 'POST',
      headers: {Accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${token}`},
      body: JSON.stringify(payload)
    });
    return {status: res.status, json: await res.json().catch(() => ({}))};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function getRepKnowledge(question, token, signal = undefined, locale = 'it') {
  if (!token) return {status: 401, json: {error: 'missing_token'}};
  const params = new URLSearchParams({q: question, locale});
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/rep/knowledge?${params.toString()}`, {
      method: 'GET',
      headers: {Accept: 'application/json', Authorization: `Bearer ${token}`},
      signal
    });
    return {status: res.status, json: await res.json().catch(() => ({}))};
  } catch (error) {
    if (error && error.name === 'AbortError') return {status: -1, json: {cancelled: true}};
    return {status: -1, json: {error: error.message}};
  }
}

export async function adminGetRepMonitoring(hours, token) {
  if (!token) return {status: 401, json: {error: 'missing_token'}};
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/admin/rep/monitoring?hours=${encodeURIComponent(hours || 24)}`, {
      headers: {Accept: 'application/json', Authorization: `Bearer ${token}`}
    });
    return {status: res.status, json: await res.json().catch(() => ({}))};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function queryRep(payload, token, signal = undefined) {
  if (!token) return {status: 401, json: {error: 'missing_token'}};
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/rep/query`, {
      method: 'POST',
      headers: {Accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${token}`},
      body: JSON.stringify(payload),
      signal
    });
    return {status: res.status, json: await res.json().catch(() => ({}))};
  } catch (error) {
    if (error && error.name === 'AbortError') return {status: -1, json: {cancelled: true}};
    return {status: -1, json: {error: error.message}};
  }
}

export async function prepareRepAction(payload, token, signal = undefined) {
  if (!token) return {status: 401, json: {error: 'missing_token'}};
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/rep/actions/prepare`, {
      method: 'POST',
      headers: {Accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${token}`},
      body: JSON.stringify(payload),
      signal
    });
    return {status: res.status, json: await res.json().catch(() => ({}))};
  } catch (error) {
    if (error && error.name === 'AbortError') return {status: -1, json: {cancelled: true}};
    return {status: -1, json: {error: error.message}};
  }
}

export async function getRepAction(actionId, token, signal = undefined) {
  if (!token) return {status: 401, json: {error: 'missing_token'}};
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/rep/actions/${encodeURIComponent(actionId)}`, {
      method: 'GET',
      headers: {Accept: 'application/json', Authorization: `Bearer ${token}`},
      signal
    });
    return {status: res.status, json: await res.json().catch(() => ({}))};
  } catch (error) {
    if (error && error.name === 'AbortError') return {status: -1, json: {cancelled: true}};
    return {status: -1, json: {error: error.message}};
  }
}

export async function confirmRepAction(actionId, idempotencyKey, token) {
  if (!token) return {status: 401, json: {error: 'missing_token'}};
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/rep/actions/${encodeURIComponent(actionId)}/confirm`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'Idempotency-Key': idempotencyKey
      },
      body: JSON.stringify({})
    });
    return {status: res.status, json: await res.json().catch(() => ({}))};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function transcribeRepAudio(audio, filename, token, language = null) {
  if (!token) return {status: 401, json: {error: {code: 'UNAUTHORIZED'}}};
  const form = new FormData();
  form.append('audio', audio, filename);
  if (language) form.append('language', language);
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/hey-rep/transcribe`, {
      method: 'POST',
      headers: {Accept: 'application/json', Authorization: `Bearer ${token}`},
      body: form
    });
    return {status: res.status, json: await res.json().catch(() => ({}))};
  } catch (error) {
    return {status: -1, json: {error: {code: 'NETWORK_ERROR', message: error.message}}};
  }
}

export async function adminGetRepFeedback(filters, token) {
  if (!token) return {status: 401, json: {error: 'missing_token'}};
  const params = new URLSearchParams();
  Object.entries(filters || {}).forEach(([key, value]) => {
    if (value !== '' && value !== null && value !== undefined) params.set(key, value);
  });
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/admin/rep/feedback?${params.toString()}`, {
      headers: {Accept: 'application/json', Authorization: `Bearer ${token}`}
    });
    return {status: res.status, json: await res.json().catch(() => ({}))};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function adminUpdateRepFeedback(itemId, payload, token) {
  if (!token) return {status: 401, json: {error: 'missing_token'}};
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/admin/rep/feedback/${itemId}`, {
      method: 'PATCH',
      headers: {Accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${token}`},
      body: JSON.stringify(payload)
    });
    return {status: res.status, json: await res.json().catch(() => ({}))};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function adminDeleteRepFeedback(itemId, token) {
  if (!token) return {status: 401, json: {error: 'missing_token'}};
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/admin/rep/feedback/${itemId}`, {
      method: 'DELETE', headers: {Accept: 'application/json', Authorization: `Bearer ${token}`}
    });
    return {status: res.status, json: await res.json().catch(() => ({}))};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}

export async function adminExportRepFeedback(filters, token) {
  if (!token) return {status: 401};
  const params = new URLSearchParams();
  Object.entries(filters || {}).forEach(([key, value]) => value && params.set(key, value));
  try {
    const res = await fetch(`${Configuration.apiEndPoint}/admin/rep/feedback/export.csv?${params.toString()}`, {
      headers: {Authorization: `Bearer ${token}`}
    });
    if (!res.ok) return {status: res.status};
    download(await res.blob(), 'rep-feedback.csv', 'text/csv');
    return {status: res.status};
  } catch (error) {
    return {status: -1, json: {error: error.message}};
  }
}
